'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSignIn } from '@clerk/nextjs'
import { desktopRedirectOrFallback } from './desktopRedirect'

export type SignInStep = 'credentials' | 'verification' | 'mfa'

interface SignInFormState {
  email: string
  password: string
  isLoading: boolean
  error: string | null
  step: SignInStep
}

const SIGN_IN_REDIRECT = process.env.NEXT_PUBLIC_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL || '/send'

export function useSignInForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { signIn, errors, fetchStatus } = useSignIn()

  function redirectAfterSignIn() {
    const target = desktopRedirectOrFallback(new URLSearchParams(searchParams.toString()), SIGN_IN_REDIRECT)
    if (target.startsWith('/api/')) {
      // Full nav so browser follows the API route's 302 to snaptransfer://
      window.location.href = target
    } else {
      router.push(target)
    }
  }

  const [state, setState] = useState<SignInFormState>({
    email: '',
    password: '',
    isLoading: false,
    error: null,
    step: 'credentials'
  })

  // Reflect Clerk field errors into local error string
  const clerkError =
    errors.fields.identifier?.code ??
    errors.fields.password?.code ??
    errors.fields.code?.code ??
    errors.global?.[0]?.code ??
    null

  function setField(field: 'email' | 'password', value: string) {
    setState((prev) => ({ ...prev, [field]: value, error: null }))
  }

  async function handleSubmit() {
    if (fetchStatus === 'fetching' || !signIn) return
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    // Step 1: identify the user
    const { error: createError } = await signIn.create({ identifier: state.email })
    if (createError) {
      setState((prev) => ({ ...prev, isLoading: false, error: createError.code }))
      return
    }

    // Step 2: submit password
    const { error: pwError } = await signIn.password({
      identifier: state.email,
      password: state.password
    })
    if (pwError) {
      setState((prev) => ({ ...prev, isLoading: false, error: pwError.code }))
      return
    }

    // Step 3: finalize and redirect
    if (signIn.status === 'complete') {
      const { error: finalizeError } = await signIn.finalize()
      if (finalizeError) {
        setState((prev) => ({ ...prev, isLoading: false, error: finalizeError.code }))
        return
      }
      redirectAfterSignIn()
      return
    }

    // Needs email code verification (first factor)
    if (signIn.status === 'needs_first_factor') {
      const { error: codeError } = await signIn.emailCode.sendCode()
      if (codeError) {
        setState((prev) => ({ ...prev, isLoading: false, error: codeError.code }))
        return
      }
      setState((prev) => ({ ...prev, isLoading: false, step: 'verification' }))
      return
    }

    // Needs a 2nd-factor email code: account MFA (needs_second_factor) OR Client
    // Trust new-device step-up (needs_client_trust). Both use the mfa email-code path.
    if (signIn.status === 'needs_second_factor' || signIn.status === 'needs_client_trust') {
      const { error: mfaError } = await signIn.mfa.sendEmailCode()
      if (mfaError) {
        setState((prev) => ({ ...prev, isLoading: false, error: mfaError.code }))
        return
      }
      setState((prev) => ({ ...prev, isLoading: false, step: 'mfa' }))
      return
    }

    setState((prev) => ({ ...prev, isLoading: false }))
  }

  async function submitCode(code: string) {
    if (fetchStatus === 'fetching' || !signIn) return
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    const verifyResult =
      state.step === 'mfa' ? await signIn.mfa.verifyEmailCode({ code }) : await signIn.emailCode.verifyCode({ code })
    if (verifyResult.error) {
      setState((prev) => ({ ...prev, isLoading: false, error: verifyResult.error!.code }))
      return
    }

    if (signIn.status === 'complete') {
      const { error: finalizeError } = await signIn.finalize()
      if (finalizeError) {
        setState((prev) => ({ ...prev, isLoading: false, error: finalizeError.code }))
        return
      }
      redirectAfterSignIn()
      return
    }
    setState((prev) => ({ ...prev, isLoading: false }))
  }

  async function resendCode() {
    if (!signIn) return
    // best-effort resend; route by step
    if (state.step === 'mfa') {
      await signIn.mfa.sendEmailCode().catch(() => undefined)
    } else {
      await signIn.emailCode.sendCode().catch(() => undefined)
    }
  }

  function goBack() {
    setState((prev) => ({ ...prev, step: 'credentials', error: null }))
  }

  const error = state.error ?? clerkError

  return {
    email: state.email,
    password: state.password,
    isLoading: state.isLoading,
    error,
    step: state.step,
    setField,
    handleSubmit,
    submitCode,
    resendCode,
    goBack
  }
}
